/**
 * .tuMetrics {"tu1": 0.9, "tu2": 0.9, "tu3": 3, "notes": "Line-level LCS diff for markdown; drives version change summaries and the History view", "tuVersion": "2.5"}
 * .help "Compares two versions of a document line by line."
 */

export type DiffOp = { type: 'equal' | 'added' | 'removed'; text: string };

export interface MarkdownDiff {
  ops: DiffOp[];
  addedCount: number;
  removedCount: number;
  /** Lines that exist in both but changed — counted as a removed+added pair, reported once. */
  summary: string;
}

/** Line diff by longest common subsequence. Fine for plan-sized documents. */
export function diffLines(before: string, after: string): DiffOp[] {
  const a = (before ?? '').replace(/\r\n/g, '\n').split('\n');
  const b = (after ?? '').replace(/\r\n/g, '\n').split('\n');
  if (a.length === 1 && a[0] === '') a.length = 0;
  if (b.length === 1 && b[0] === '') b.length = 0;

  const n = a.length;
  const m = b.length;
  // lcs[i][j] = LCS length of a[i..], b[j..]
  const lcs: Uint32Array[] = new Array(n + 1);
  for (let i = 0; i <= n; i++) lcs[i] = new Uint32Array(m + 1);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: 'equal', text: a[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      ops.push({ type: 'removed', text: a[i++] });
    } else {
      ops.push({ type: 'added', text: b[j++] });
    }
  }
  while (i < n) ops.push({ type: 'removed', text: a[i++] });
  while (j < m) ops.push({ type: 'added', text: b[j++] });
  return ops;
}

export function diffMarkdown(before: string, after: string): MarkdownDiff {
  const ops = diffLines(before, after);
  const addedCount = ops.filter((o) => o.type === 'added' && o.text.trim()).length;
  const removedCount = ops.filter((o) => o.type === 'removed' && o.text.trim()).length;
  const parts: string[] = [];
  if (addedCount) parts.push(`${addedCount} line${addedCount === 1 ? '' : 's'} added`);
  if (removedCount) parts.push(`${removedCount} removed`);
  return { ops, addedCount, removedCount, summary: parts.length ? parts.join(', ') : 'No text changes' };
}
